import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  SearchIcon,
  RefreshIcon,
  DownloadIcon,
  CheckCircleIcon,
  AlertIcon,
  ClockIcon,
  FilterIcon,
  SettingsIcon,
  ActivityIcon,
  ShieldIcon,
  ServerIcon,
  DatabaseIcon,
  TrashIcon,
  CpuIcon
} from '../components/common/Icons';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet';

// ============================================================================
// TYPES
// ============================================================================

type LogType = 'audit' | 'security' | 'system' | 'api' | 'ai';
type LogLevel = 'debug' | 'info' | 'warning' | 'error' | 'critical';

interface LogEntry {
  id: string;
  timestamp: string;
  log_type: LogType;
  level: LogLevel;
  source: string;
  message: string;
  user_id?: string;
  user_email?: string;
  ip_address?: string;
  action?: string;
  resource_type?: string;
  resource_id?: string;
  details?: Record<string, any>;
  duration_ms?: number;
  status_code?: number;
  request_path?: string;
  success: boolean;
}

interface LogStats {
  log_type: LogType;
  total_entries: number;
  entries_today: number;
  entries_this_week: number;
  error_count: number;
  warning_count: number;
  oldest_entry?: string;
  newest_entry?: string;
  storage_estimate_mb: number;
}

interface LogSummary {
  total_entries: number;
  by_type: Record<string, number>;
  by_level: Record<string, number>;
  retention_config: RetentionConfig;
  storage_total_mb: number;
  last_cleanup?: string;
}

interface RetentionConfig {
  audit_retention_days: number;
  security_retention_days: number;
  system_retention_days: number;
  api_retention_days: number;
  ai_retention_days: number;
  max_entries_per_type: number;
  auto_cleanup_enabled: boolean;
}

// ============================================================================
// LOG TYPE DEFINITIONS
// ============================================================================

const LOG_TYPES: { id: LogType; label: string; icon: React.ReactNode; description: string; color: string }[] = [
  { 
    id: 'audit', 
    label: 'Audit', 
    icon: <ClockIcon size={18} />, 
    description: 'User actions and changes',
    color: 'var(--accent-primary)'
  },
  { 
    id: 'security', 
    label: 'Security', 
    icon: <ShieldIcon size={18} />, 
    description: 'Authentication and access',
    color: 'var(--warning)'
  },
  { 
    id: 'system', 
    label: 'System', 
    icon: <ServerIcon size={18} />, 
    description: 'Application events',
    color: 'var(--info)'
  },
  {
    id: 'api',
    label: 'API',
    icon: <ActivityIcon size={18} />,
    description: 'Request/response logs',
    color: 'var(--success)'
  },
  {
    id: 'ai',
    label: 'AI',
    icon: <CpuIcon size={18} />,
    description: 'AI model and agent events',
    color: 'var(--accent-secondary)'
  },
];

const LOG_LEVELS: { id: LogLevel; label: string; color: string }[] = [
  { id: 'debug', label: 'Debug', color: 'var(--text-tertiary)' },
  { id: 'info', label: 'Info', color: 'var(--info)' },
  { id: 'warning', label: 'Warning', color: 'var(--warning)' },
  { id: 'error', label: 'Error', color: 'var(--error)' },
  { id: 'critical', label: 'Critical', color: '#dc2626' },
];

// ============================================================================
// COMPONENT
// ============================================================================

const LogsPage: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState<LogType | 'all'>('all');
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [summary, setSummary] = useState<LogSummary | null>(null);
  const [stats, setStats] = useState<Record<LogType, LogStats>>({} as Record<LogType, LogStats>);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState<LogLevel | ''>('');
  const [dateFilter, setDateFilter] = useState<'1h' | '24h' | '7d' | '30d' | 'all'>('24h');

  // Auto-refresh settings
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5000); // 5 seconds

  // Detail drawer
  const [selectedEntry, setSelectedEntry] = useState<LogEntry | null>(null);

  // Settings modal
  const [showSettings, setShowSettings] = useState(false);
  const [retentionConfig, setRetentionConfig] = useState<RetentionConfig | null>(null);
  const [isSavingConfig, setIsSavingConfig] = useState(false);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Build query params
      const params = new URLSearchParams();
      if (activeTab !== 'all') params.append('log_type', activeTab);
      if (levelFilter) params.append('level', levelFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      // Date filter
      if (dateFilter !== 'all') {
        const now = new Date();
        let startDate: Date;
        switch (dateFilter) {
          case '1h': startDate = new Date(now.getTime() - 60 * 60 * 1000); break;
          case '24h': startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000); break;
          case '7d': startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); break;
          case '30d': startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); break;
          default: startDate = new Date(0);
        }
        params.append('start_date', startDate.toISOString());
      }
      
      params.append('limit', '500');
      
      const response = await authFetch(`/api/admin/logs?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data.entries || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to fetch logs');
      }
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      setError('Failed to connect to API');
    }
    
    setIsLoading(false);
  }, [authFetch, activeTab, levelFilter, searchQuery, dateFilter]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await authFetch('/api/admin/logs/summary');
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
        setRetentionConfig(data.retention_config);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  }, [authFetch]);

  const fetchStats = useCallback(async () => {
    try {
      const statsData: Record<LogType, LogStats> = {} as Record<LogType, LogStats>;
      for (const logType of LOG_TYPES) {
        const response = await authFetch(`/api/admin/logs/stats/${logType.id}`);
        if (response.ok) {
          statsData[logType.id] = await response.json();
        }
      }
      setStats(statsData);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [authFetch]);

  useEffect(() => {
    if (authLoading) return;
    fetchLogs();
    fetchSummary();
    fetchStats();
  }, [authLoading, fetchLogs, fetchSummary, fetchStats]);

  // Auto-refresh polling
  useEffect(() => {
    if (!autoRefresh || authLoading) return;

    const interval = setInterval(() => {
      fetchLogs();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, authLoading, fetchLogs]);

  // ============================================================================
  // ACTIONS
  // ============================================================================

  const handleExport = async (format: 'json' | 'csv') => {
    try {
      const params = new URLSearchParams();
      if (activeTab !== 'all') params.append('log_type', activeTab);
      params.append('format', format);
      
      const response = await authFetch(`/api/admin/logs/export?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data.entries, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `logs-${activeTab}-${new Date().toISOString().split('T')[0]}.${format}`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      alert('Failed to export logs');
    }
  };

  const handleCleanup = async () => {
    if (!confirm('Are you sure you want to cleanup old logs based on retention settings?')) return;
    
    try {
      const response = await authFetch('/api/admin/logs/cleanup', { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        alert(`Cleanup completed. Deleted: ${JSON.stringify(data.deleted)}`);
        fetchLogs();
        fetchSummary();
        fetchStats();
      }
    } catch (err) {
      alert('Failed to cleanup logs');
    }
  };

  const handleSaveRetention = async () => {
    if (!retentionConfig) return;
    
    setIsSavingConfig(true);
    try {
      const response = await authFetch('/api/admin/logs/retention', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(retentionConfig),
      });
      
      if (response.ok) {
        const data = await response.json();
        setRetentionConfig(data);
        setShowSettings(false);
        fetchSummary();
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.detail || 'Failed to save retention settings');
      }
    } catch (err) {
      alert('Failed to save retention settings');
    }
    setIsSavingConfig(false);
  };

  // ============================================================================
  // HELPERS
  // ============================================================================

  const getLevelBadgeClass = (level: LogLevel): string => {
    switch (level) {
      case 'debug': return 'badge-secondary';
      case 'info': return 'badge-info';
      case 'warning': return 'badge-warning';
      case 'error': return 'badge-error';
      case 'critical': return 'badge-error';
      default: return 'badge-secondary';
    }
  };

  const getLevelIcon = (level: LogLevel) => {
    switch (level) {
      case 'info': return <CheckCircleIcon size={14} />;
      case 'warning': return <AlertIcon size={14} />;
      case 'error': return <AlertIcon size={14} />;
      case 'critical': return <AlertIcon size={14} />;
      default: return null;
    }
  };

  const formatTimestamp = (ts: string): string => {
    try {
      const date = new Date(ts);
      return date.toLocaleString();
    } catch {
      return ts;
    }
  };

  const formatDuration = (ms?: number): string => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="page-container" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">
            <DatabaseIcon size={28} style={{ marginRight: '12px' }} />
            Logs
          </h1>
          <p className="page-subtitle">
            Platform logging and audit trail
            {summary && ` • ${summary.total_entries.toLocaleString()} total entries`}
          </p>
        </div>
        <div className="page-header-right" style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-secondary" onClick={() => handleExport('json')}>
            <DownloadIcon size={16} />
            Export
          </button>
          <button className="btn btn-secondary" onClick={handleCleanup} title="Cleanup old logs">
            <TrashIcon size={16} />
            Cleanup
          </button>
          <button className="btn btn-secondary" onClick={() => setShowSettings(true)}>
            <SettingsIcon size={16} />
            Retention
          </button>
          <button className="btn btn-ghost" onClick={fetchLogs}>
            <RefreshIcon size={18} />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', marginBottom: '24px' }}>
          {LOG_TYPES.map((logType) => {
            const typeStats = stats[logType.id];
            return (
              <div
                key={logType.id}
                className="glass-card"
                style={{
                  padding: '16px',
                  cursor: 'pointer',
                  borderLeft: `3px solid ${logType.color}`,
                  opacity: activeTab === logType.id || activeTab === 'all' ? 1 : 0.6,
                }}
                onClick={() => setActiveTab(activeTab === logType.id ? 'all' : logType.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ color: logType.color }}>{logType.icon}</span>
                  <span style={{ fontWeight: 600 }}>{logType.label}</span>
                </div>
                <div style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>
                  {(summary.by_type[logType.id] || 0).toLocaleString()}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                  {typeStats ? `${typeStats.entries_today} today • ${typeStats.error_count} errors` : logType.description}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="page-toolbar" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search */}
          <div className="input-wrapper" style={{ maxWidth: '300px' }}>
            <SearchIcon size={18} className="input-icon" />
            <input
              type="text"
              className="input input-with-icon"
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchLogs()}
            />
          </div>

          {/* Log Type Tabs */}
          <div style={{ display: 'flex', gap: '4px', background: 'var(--glass-bg)', borderRadius: '8px', padding: '4px' }}>
            <button
              className={`btn btn-sm ${activeTab === 'all' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setActiveTab('all')}
            >
              All
            </button>
            {LOG_TYPES.map((lt) => (
              <button
                key={lt.id}
                className={`btn btn-sm ${activeTab === lt.id ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setActiveTab(lt.id)}
              >
                {lt.label}
              </button>
            ))}
          </div>

          {/* Level Filter */}
          <select
            className="input"
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value as LogLevel | '')}
            style={{ width: '140px' }}
          >
            <option value="">All Levels</option>
            {LOG_LEVELS.map((level) => (
              <option key={level.id} value={level.id}>{level.label}</option>
            ))}
          </select>

          {/* Date Filter */}
          <select
            className="input"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value as any)}
            style={{ width: '140px' }}
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>

          <button className="btn btn-primary btn-sm" onClick={fetchLogs}>
            <FilterIcon size={14} />
            Apply
          </button>

          {/* Auto-refresh toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '16px', paddingLeft: '16px', borderLeft: '1px solid var(--border-color)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px' }}>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              Auto-refresh
            </label>
            {autoRefresh && (
              <select
                className="input"
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
                style={{ width: '90px', padding: '4px 8px', fontSize: '12px' }}
              >
                <option value={2000}>2s</option>
                <option value={5000}>5s</option>
                <option value={10000}>10s</option>
                <option value={30000}>30s</option>
              </select>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* Log Table */}
      <div className="glass-card" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {isLoading ? (
          <div className="loading-state" style={{ padding: '48px', textAlign: 'center' }}>
            <div className="spinner" />
            <p>Loading logs...</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="empty-state" style={{ padding: '48px', textAlign: 'center' }}>
            <DatabaseIcon size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
            <p>No log entries found</p>
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table className="data-table" style={{ width: '100%' }}>
              <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-secondary)', zIndex: 10 }}>
                <tr>
                  <th style={{ width: '160px' }}>Timestamp</th>
                  <th style={{ width: '80px' }}>Type</th>
                  <th style={{ width: '100px' }}>Level</th>
                  <th style={{ width: '120px' }}>Source</th>
                  <th>Message</th>
                  <th style={{ width: '140px' }}>User</th>
                  <th style={{ width: '100px' }}>IP</th>
                  <th style={{ width: '80px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={entry.id}
                    style={{
                      opacity: entry.success ? 1 : 0.9,
                      cursor: 'pointer',
                      background: selectedEntry?.id === entry.id ? 'var(--glass-bg)' : undefined,
                    }}
                    onClick={() => setSelectedEntry(entry)}
                  >
                    <td className="font-mono text-sm" style={{ fontSize: '12px' }}>
                      {formatTimestamp(entry.timestamp)}
                    </td>
                    <td>
                      <span
                        className="badge"
                        style={{
                          background: LOG_TYPES.find(lt => lt.id === entry.log_type)?.color + '20',
                          color: LOG_TYPES.find(lt => lt.id === entry.log_type)?.color,
                          fontSize: '10px',
                          padding: '2px 6px',
                        }}
                      >
                        {entry.log_type}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${getLevelBadgeClass(entry.level)}`} style={{ fontSize: '10px' }}>
                        {getLevelIcon(entry.level)}
                        <span style={{ marginLeft: '4px' }}>{entry.level}</span>
                      </span>
                    </td>
                    <td className="font-mono text-sm" style={{ fontSize: '12px' }}>
                      {entry.source}
                    </td>
                    <td style={{ maxWidth: '400px' }}>
                      <div style={{ 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        fontSize: '13px'
                      }}>
                        {entry.message}
                      </div>
                      {entry.resource_id && (
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                          {entry.resource_type}: {entry.resource_id}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: '12px' }}>
                      {entry.user_email || '-'}
                    </td>
                    <td className="font-mono" style={{ fontSize: '11px' }}>
                      {entry.ip_address || '-'}
                    </td>
                    <td>
                      {entry.log_type === 'api' && entry.status_code ? (
                        <span style={{
                          color: entry.status_code < 400 ? 'var(--success)' : entry.status_code < 500 ? 'var(--warning)' : 'var(--error)',
                          fontWeight: 500,
                          fontSize: '12px'
                        }}>
                          {entry.status_code} ({formatDuration(entry.duration_ms)})
                        </span>
                      ) : (
                        <span style={{ color: entry.success ? 'var(--success)' : 'var(--error)', fontSize: '12px' }}>
                          {entry.success ? '✓' : '✗'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Retention Settings Modal */}
      {showSettings && retentionConfig && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowSettings(false)}
        >
          <div
            className="glass-card"
            style={{
              width: '500px',
              maxHeight: '80vh',
              overflow: 'auto',
              padding: '24px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <SettingsIcon size={24} />
              Log Retention Settings
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontSize: '14px' }}>
              Configure how long logs are retained before automatic cleanup.
              Lower values save storage but reduce audit history.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Audit Retention */}
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                  Audit Log Retention (days)
                </label>
                <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
                  Compliance logs - recommended 90+ days
                </p>
                <input
                  type="number"
                  className="input"
                  min={7}
                  max={365}
                  value={retentionConfig.audit_retention_days}
                  onChange={(e) => setRetentionConfig({ ...retentionConfig, audit_retention_days: parseInt(e.target.value) || 90 })}
                />
              </div>

              {/* Security Retention */}
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                  Security Log Retention (days)
                </label>
                <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
                  Auth & access logs - recommended 30+ days
                </p>
                <input
                  type="number"
                  className="input"
                  min={7}
                  max={180}
                  value={retentionConfig.security_retention_days}
                  onChange={(e) => setRetentionConfig({ ...retentionConfig, security_retention_days: parseInt(e.target.value) || 30 })}
                />
              </div>

              {/* System Retention */}
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                  System Log Retention (days)
                </label>
                <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
                  Application logs - recommended 14+ days
                </p>
                <input
                  type="number"
                  className="input"
                  min={3}
                  max={90}
                  value={retentionConfig.system_retention_days}
                  onChange={(e) => setRetentionConfig({ ...retentionConfig, system_retention_days: parseInt(e.target.value) || 14 })}
                />
              </div>

              {/* API Retention */}
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                  API Log Retention (days)
                </label>
                <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
                  Request logs - high volume, recommended 7 days
                </p>
                <input
                  type="number"
                  className="input"
                  min={1}
                  max={30}
                  value={retentionConfig.api_retention_days}
                  onChange={(e) => setRetentionConfig({ ...retentionConfig, api_retention_days: parseInt(e.target.value) || 7 })}
                />
              </div>

              {/* AI Retention */}
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                  AI Log Retention (days)
                </label>
                <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
                  AI model and agent logs - recommended 7 days
                </p>
                <input
                  type="number"
                  className="input"
                  min={1}
                  max={30}
                  value={retentionConfig.ai_retention_days}
                  onChange={(e) => setRetentionConfig({ ...retentionConfig, ai_retention_days: parseInt(e.target.value) || 7 })}
                />
              </div>

              {/* Max Entries */}
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                  Max Entries Per Type
                </label>
                <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '8px' }}>
                  Memory limit per log type (1,000 - 100,000)
                </p>
                <input
                  type="number"
                  className="input"
                  min={1000}
                  max={100000}
                  step={1000}
                  value={retentionConfig.max_entries_per_type}
                  onChange={(e) => setRetentionConfig({ ...retentionConfig, max_entries_per_type: parseInt(e.target.value) || 10000 })}
                />
              </div>

              {/* Auto Cleanup */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="auto-cleanup"
                  checked={retentionConfig.auto_cleanup_enabled}
                  onChange={(e) => setRetentionConfig({ ...retentionConfig, auto_cleanup_enabled: e.target.checked })}
                />
                <label htmlFor="auto-cleanup" style={{ fontWeight: 500 }}>
                  Enable automatic cleanup
                </label>
              </div>

              {/* Storage Estimate */}
              {summary && (
                <div style={{ 
                  padding: '12px', 
                  background: 'var(--glass-bg)', 
                  borderRadius: '8px',
                  fontSize: '13px'
                }}>
                  <strong>Current Storage:</strong> ~{summary.storage_total_mb.toFixed(2)} MB
                  {summary.last_cleanup && (
                    <span style={{ marginLeft: '16px', color: 'var(--text-tertiary)' }}>
                      Last cleanup: {formatTimestamp(summary.last_cleanup)}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowSettings(false)}>
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSaveRetention}
                disabled={isSavingConfig}
              >
                {isSavingConfig ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Log Detail Drawer */}
      <Sheet open={!!selectedEntry} onOpenChange={(open) => { if (!open) setSelectedEntry(null); }}>
        <SheetContent
          side="right"
          className="sheet-glass"
          style={{
            width: '480px',
            maxWidth: '90vw',
            background: 'var(--bg-secondary)',
            borderLeft: '1px solid var(--border-color)',
            overflowY: 'auto',
          }}
        >
          {selectedEntry && (
            <>
              <SheetHeader>
                <SheetTitle style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span
                    className="badge"
                    style={{
                      background: LOG_TYPES.find(lt => lt.id === selectedEntry.log_type)?.color + '20',
                      color: LOG_TYPES.find(lt => lt.id === selectedEntry.log_type)?.color,
                      fontSize: '11px',
                      padding: '2px 8px',
                    }}
                  >
                    {selectedEntry.log_type.toUpperCase()}
                  </span>
                  <span className={`badge ${getLevelBadgeClass(selectedEntry.level)}`} style={{ fontSize: '11px' }}>
                    {selectedEntry.level.toUpperCase()}
                  </span>
                  {!selectedEntry.success && (
                    <span className="badge badge-error" style={{ fontSize: '11px' }}>FAILED</span>
                  )}
                </SheetTitle>
                <SheetDescription style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {selectedEntry.timestamp}
                </SheetDescription>
              </SheetHeader>

              <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Message */}
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Message</div>
                  <div style={{ fontSize: '14px', lineHeight: 1.5, wordBreak: 'break-word' }}>
                    {selectedEntry.message}
                  </div>
                </div>

                {/* Identity Section */}
                {(selectedEntry.user_email || selectedEntry.user_id || selectedEntry.ip_address) && (
                  <div style={{ padding: '12px', background: 'var(--glass-bg)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Identity</div>
                    {selectedEntry.user_email && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Email</span>
                        <span style={{ fontFamily: 'monospace' }}>{selectedEntry.user_email}</span>
                      </div>
                    )}
                    {selectedEntry.user_id && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>User ID</span>
                        <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>{selectedEntry.user_id}</span>
                      </div>
                    )}
                    {selectedEntry.ip_address && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>IP Address</span>
                        <span style={{ fontFamily: 'monospace' }}>{selectedEntry.ip_address}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Section */}
                {(selectedEntry.action || selectedEntry.resource_type || selectedEntry.resource_id) && (
                  <div style={{ padding: '12px', background: 'var(--glass-bg)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Action</div>
                    {selectedEntry.action && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Action</span>
                        <span style={{ fontFamily: 'monospace' }}>{selectedEntry.action}</span>
                      </div>
                    )}
                    {selectedEntry.resource_type && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Resource Type</span>
                        <span>{selectedEntry.resource_type}</span>
                      </div>
                    )}
                    {selectedEntry.resource_id && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Resource ID</span>
                        <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>{selectedEntry.resource_id}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Request Section (API logs) */}
                {(selectedEntry.request_path || selectedEntry.status_code || selectedEntry.duration_ms) && (
                  <div style={{ padding: '12px', background: 'var(--glass-bg)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Request</div>
                    {selectedEntry.request_path && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Path</span>
                        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{selectedEntry.request_path}</span>
                      </div>
                    )}
                    {selectedEntry.status_code && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Status Code</span>
                        <span style={{
                          fontWeight: 600,
                          color: selectedEntry.status_code < 400 ? 'var(--success)' : selectedEntry.status_code < 500 ? 'var(--warning)' : 'var(--error)',
                        }}>{selectedEntry.status_code}</span>
                      </div>
                    )}
                    {selectedEntry.duration_ms != null && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Duration</span>
                        <span>{formatDuration(selectedEntry.duration_ms)}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Details JSON Section */}
                {selectedEntry.details && Object.keys(selectedEntry.details).length > 0 && (
                  <div style={{ padding: '12px', background: 'var(--glass-bg)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Details</div>
                    <pre style={{
                      fontSize: '12px',
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      margin: 0,
                      padding: '8px',
                      background: 'rgba(0,0,0,0.2)',
                      borderRadius: '4px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                    }}>
                      {JSON.stringify(selectedEntry.details, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Source + Entry ID */}
                <div style={{ padding: '12px', background: 'var(--glass-bg)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Metadata</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '13px' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Source</span>
                    <span style={{ fontFamily: 'monospace' }}>{selectedEntry.source}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Entry ID</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '10px' }}>{selectedEntry.id}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default LogsPage;
